// A small, dependency-free evaluator for the row-expression strings used by
// TableWithInputColumn.expressions (see irembo-table-with-input.model.ts) --
// deliberately NOT `new Function(...)`/eval: those construct and run code
// from an arbitrary string, which static-analysis security tooling (and
// genuine defense-in-depth, since these strings come from service-manager-
// authored form config, not code review) rightly flags. This supports
// exactly the grammar these expressions actually use -- member access
// (plain and optional-chained), literals, unary "!"/"-", the arithmetic/
// comparison/logical/nullish-coalescing operators, and the ternary -- by
// parsing to a small AST and interpreting that, rather than executing
// arbitrary JavaScript.

type Token =
  | { type: 'num'; value: number }
  | { type: 'str'; value: string }
  | { type: 'ident'; value: string }
  | { type: 'punct'; value: string };

// Indexed by punctuator length (index 0 is unused) so the tokenizer can try
// the longest match first without one near-identical branch per length.
const PUNCTUATORS_BY_LENGTH: readonly (readonly string[])[] = [
  [],
  ['(', ')', '.', '?', ':', '+', '-', '*', '/', '%', '!', '<', '>'],
  ['?.', '??', '&&', '||', '==', '!=', '<=', '>='],
  ['===', '!=='],
];

// Advances from `start` while `pattern` matches the current character,
// returning the first index that no longer matches (or source.length).
// Shared by the number and identifier scanners below, which differ only in
// which characters are allowed.
function scanWhileMatching(
  source: string,
  start: number,
  pattern: RegExp
): number {
  let j = start;
  while (j < source.length && pattern.test(source[j])) {
    j++;
  }
  return j;
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = source.length;
  while (i < n) {
    const ch = source[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === "'" || ch === '"') {
      const quote = ch;
      let j = i + 1;
      let value = '';
      while (j < n && source[j] !== quote) {
        if (source[j] === '\\' && j + 1 < n) {
          value += source[j + 1];
          j += 2;
        } else {
          value += source[j];
          j++;
        }
      }
      if (j >= n) {
        throw new Error(`Unterminated string literal in expression`);
      }
      tokens.push({ type: 'str', value });
      i = j + 1;
      continue;
    }
    if (/[0-9]/.test(ch)) {
      const j = scanWhileMatching(source, i, /[0-9.]/);
      tokens.push({ type: 'num', value: Number(source.slice(i, j)) });
      i = j;
      continue;
    }
    if (/[A-Za-z_$]/.test(ch)) {
      const j = scanWhileMatching(source, i, /[A-Za-z0-9_$]/);
      tokens.push({ type: 'ident', value: source.slice(i, j) });
      i = j;
      continue;
    }
    const punctLength = [3, 2, 1].find(len =>
      PUNCTUATORS_BY_LENGTH[len].includes(source.slice(i, i + len))
    );
    if (!punctLength) {
      throw new Error(`Unexpected character "${ch}" in expression`);
    }
    tokens.push({ type: 'punct', value: source.slice(i, i + punctLength) });
    i += punctLength;
  }
  return tokens;
}

type ExprNode =
  | { type: 'lit'; value: string | number | boolean | null | undefined }
  | { type: 'ident'; name: string }
  | { type: 'member'; object: ExprNode; property: string; optional: boolean }
  | { type: 'unary'; op: '!' | '-'; argument: ExprNode }
  | { type: 'binary'; op: string; left: ExprNode; right: ExprNode }
  | { type: 'logical'; op: '&&' | '||' | '??'; left: ExprNode; right: ExprNode }
  | {
      type: 'conditional';
      test: ExprNode;
      consequent: ExprNode;
      alternate: ExprNode;
    };

const EQUALITY_OPERATORS = ['===', '!==', '==', '!='];
const RELATIONAL_OPERATORS = ['<', '<=', '>', '>='];
const ADDITIVE_OPERATORS = ['+', '-'];
const MULTIPLICATIVE_OPERATORS = ['*', '/', '%'];

class ExpressionParser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  parse(): ExprNode {
    const node = this.parseConditional();
    if (this.pos < this.tokens.length) {
      throw new Error('Unexpected trailing tokens in expression');
    }
    return node;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private isPunct(value: string): boolean {
    const token = this.peek();
    return !!token && token.type === 'punct' && token.value === value;
  }

  private consumePunct(value: string): boolean {
    if (this.isPunct(value)) {
      this.pos++;
      return true;
    }
    return false;
  }

  private expectPunct(value: string): void {
    if (!this.consumePunct(value)) {
      throw new Error(`Expected "${value}" in expression`);
    }
  }

  private parseConditional(): ExprNode {
    const test = this.parseNullish();
    if (this.consumePunct('?')) {
      const consequent = this.parseConditional();
      this.expectPunct(':');
      const alternate = this.parseConditional();
      return { type: 'conditional', test, consequent, alternate };
    }
    return test;
  }

  // Every binary/logical precedence level (nullish, or, and, equality,
  // relational, additive, multiplicative) is the same shape: parse the next
  // tighter-binding level, then greedily consume same-precedence operators
  // left-associatively. Shared here instead of one near-identical method per
  // level to avoid duplicating that shape seven times over.
  private parseLeftAssociative(
    operators: readonly string[],
    nodeType: 'binary' | 'logical',
    next: () => ExprNode
  ): ExprNode {
    let left = next();
    for (;;) {
      const token = this.peek();
      if (
        !token ||
        token.type !== 'punct' ||
        !operators.includes(token.value)
      ) {
        break;
      }
      this.pos++;
      left = {
        type: nodeType,
        op: token.value,
        left,
        right: next(),
      } as ExprNode;
    }
    return left;
  }

  private parseNullish(): ExprNode {
    return this.parseLeftAssociative(['??'], 'logical', () => this.parseOr());
  }

  private parseOr(): ExprNode {
    return this.parseLeftAssociative(['||'], 'logical', () => this.parseAnd());
  }

  private parseAnd(): ExprNode {
    return this.parseLeftAssociative(['&&'], 'logical', () =>
      this.parseEquality()
    );
  }

  private parseEquality(): ExprNode {
    return this.parseLeftAssociative(EQUALITY_OPERATORS, 'binary', () =>
      this.parseRelational()
    );
  }

  private parseRelational(): ExprNode {
    return this.parseLeftAssociative(RELATIONAL_OPERATORS, 'binary', () =>
      this.parseAdditive()
    );
  }

  private parseAdditive(): ExprNode {
    return this.parseLeftAssociative(ADDITIVE_OPERATORS, 'binary', () =>
      this.parseMultiplicative()
    );
  }

  private parseMultiplicative(): ExprNode {
    return this.parseLeftAssociative(MULTIPLICATIVE_OPERATORS, 'binary', () =>
      this.parseUnary()
    );
  }

  private parseUnary(): ExprNode {
    const token = this.peek();
    if (
      token?.type === 'punct' &&
      (token.value === '!' || token.value === '-')
    ) {
      this.pos++;
      return { type: 'unary', op: token.value, argument: this.parseUnary() };
    }
    return this.parsePostfix();
  }

  private parsePostfix(): ExprNode {
    let node = this.parsePrimary();
    for (;;) {
      const optional = this.consumePunct('?.');
      if (!optional && !this.consumePunct('.')) {
        break;
      }
      const property = this.consumeIdent();
      node = { type: 'member', object: node, property, optional };
    }
    return node;
  }

  private consumeIdent(): string {
    const token = this.peek();
    if (!token || token.type !== 'ident') {
      throw new Error('Expected a property name in expression');
    }
    this.pos++;
    return token.value;
  }

  private parsePrimary(): ExprNode {
    const token = this.peek();
    if (!token) {
      throw new Error('Unexpected end of expression');
    }
    if (token.type === 'num' || token.type === 'str') {
      this.pos++;
      return { type: 'lit', value: token.value };
    }
    if (token.type === 'punct' && token.value === '(') {
      this.pos++;
      const node = this.parseConditional();
      this.expectPunct(')');
      return node;
    }
    if (token.type === 'ident') {
      this.pos++;
      switch (token.value) {
        case 'true':
          return { type: 'lit', value: true };
        case 'false':
          return { type: 'lit', value: false };
        case 'null':
          return { type: 'lit', value: null };
        case 'undefined':
          return { type: 'lit', value: undefined };
        default:
          return { type: 'ident', name: token.value };
      }
    }
    throw new Error('Unexpected token in expression');
  }
}

function evaluateNode(node: ExprNode, scope: Record<string, unknown>): unknown {
  switch (node.type) {
    case 'lit':
      return node.value;
    case 'ident':
      return scope[node.name];
    case 'member': {
      const target = evaluateNode(node.object, scope);
      if (target === null || target === undefined) {
        if (node.optional) {
          return undefined;
        }
        throw new Error(
          `Cannot read property "${node.property}" of ${String(target)}`
        );
      }
      return (target as Record<string, unknown>)[node.property];
    }
    case 'unary': {
      const value = evaluateNode(node.argument, scope);
      return node.op === '!' ? !value : -(value as number);
    }
    case 'binary': {
      const left = evaluateNode(node.left, scope);
      const right = evaluateNode(node.right, scope);
      switch (node.op) {
        // `+` is deliberately untyped (any): it's the one operator that must
        // support both numeric addition and string concatenation, matching
        // how these expressions actually get used (e.g. joining a quantity
        // and a unit into one display string).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        case '+':
          return (left as any) + (right as any);
        case '-':
          return (left as number) - (right as number);
        case '*':
          return (left as number) * (right as number);
        case '/':
          return (left as number) / (right as number);
        case '%':
          return (left as number) % (right as number);
        case '===':
          return left === right;
        case '!==':
          return left !== right;
        // eslint-disable-next-line eqeqeq
        case '==':
          return left == right;
        // eslint-disable-next-line eqeqeq
        case '!=':
          return left != right;
        case '<':
          return (left as number) < (right as number);
        case '<=':
          return (left as number) <= (right as number);
        case '>':
          return (left as number) > (right as number);
        case '>=':
          return (left as number) >= (right as number);
        default:
          throw new Error(`Unsupported operator "${node.op}"`);
      }
    }
    case 'logical': {
      const left = evaluateNode(node.left, scope);
      if (node.op === '&&') {
        return left ? evaluateNode(node.right, scope) : left;
      }
      if (node.op === '||') {
        return left ? left : evaluateNode(node.right, scope);
      }
      return left !== null && left !== undefined
        ? left
        : evaluateNode(node.right, scope);
    }
    case 'conditional':
      return evaluateNode(node.test, scope)
        ? evaluateNode(node.consequent, scope)
        : evaluateNode(node.alternate, scope);
    default:
      throw new Error('Unsupported expression node');
  }
}

// Evaluates a row-expression string (e.g. "(model?.quantity ?? '') + ' ' +
// (model?.unitOfMeasure ?? '')") against the given scope -- typically
// `{ model: <row values> }` -- without executing arbitrary JavaScript.
export function evaluateSafeExpression(
  expression: string,
  scope: Record<string, unknown>
): unknown {
  const tokens = tokenize(expression);
  const ast = new ExpressionParser(tokens).parse();
  return evaluateNode(ast, scope);
}
